import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Avatar,
  LinearProgress,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Visibility,
  Edit,
  CheckCircle,
  Pending,
  Schedule,
  Close,
  Refresh,
  FilterList,
  Person,
  School
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const AdmissionManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Statistics
  const [stats, setStats] = useState({
    total_approved: 0,
    pass: 0,
    waiting: 0,
    failed: 0,
    absent: 0,
    admitted: 0,
    not_admitted: 0
  });
  
  // Students
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  
  // Filters
  const [entranceStatusFilter, setEntranceStatusFilter] = useState('all');
  const [admissionStatusFilter, setAdmissionStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Status update form
  const [statusForm, setStatusForm] = useState({
    entrance_status: '',
    admission_status: '',
    notes: ''
  });

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admissions/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch approved students
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (entranceStatusFilter !== 'all') {
        params.entrance_status = entranceStatusFilter;
      }
      if (admissionStatusFilter !== 'all') {
        params.admission_status = admissionStatusFilter;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      const response = await axios.get('/api/admissions/students', { params });
      
      if (response.data.success) {
        setStudents(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      enqueueSnackbar('Failed to fetch students', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch student details
  const fetchStudentDetails = async (studentId) => {
    try {
      const response = await axios.get(`/api/admissions/student/${studentId}`);
      if (response.data.success) {
        setSelectedStudent(response.data.data);
        setStatusForm({
          entrance_status: response.data.data.entrance_status || '',
          admission_status: response.data.data.admission_status || '',
          notes: response.data.data.notes || ''
        });
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
      enqueueSnackbar('Failed to fetch student details', { variant: 'error' });
    }
  };

  // Update student status
  const updateStudentStatus = async () => {
    try {
      const payload = {
        entrance_status: statusForm.entrance_status,
        admission_status: statusForm.admission_status,
        notes: statusForm.notes
      };
      
      const response = await axios.put(
        `/api/admissions/student/${selectedStudent.student_id}/status`,
        payload
      );
      
      if (response.data.success) {
        enqueueSnackbar('Student status updated successfully', { variant: 'success' });
        setStatusDialogOpen(false);
        fetchStudents();
        fetchStats();
        
        if (statusForm.admission_status === 'admitted') {
          enqueueSnackbar(
            'Student has been admitted and transferred to Student Management automatically.',
            { variant: 'info' }
          );
        }
      }
    } catch (error) {
      console.error('Error updating student status:', error);
      enqueueSnackbar('Failed to update student status', { variant: 'error' });
    }
  };

  // Create student user account
  const createStudentUser = async (student) => {
    try {
      const payload = {
        first_name: student.first_name,
        last_name: student.last_name,
        email: student.email,
        phone: student.phone,
        date_of_birth: student.date_of_birth,
        role: 'student',
        student_id: student.application_id,
        tracker_id: student.tracker_id || student.tracking_id
      };
      
      const response = await axios.post('/api/admissions/create-student-user', payload);
      
      if (response.data.success) {
        enqueueSnackbar('Student user account created successfully', { variant: 'success' });
        return response.data;
      }
      return { success: false, message: 'Failed to create user account' };
    } catch (error) {
      console.error('Error creating student user:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create student user account';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return { success: false, message: errorMessage };
    }
  };

  // Get entrance status color
  const getEntranceStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'success';
      case 'waiting': return 'warning';
      case 'failed': return 'error';
      case 'absent': return 'default';
      default: return 'default';
    }
  };

  // Get admission status color
  const getAdmissionStatusColor = (status) => {
    switch (status) {
      case 'admitted': return 'success';
      case 'not_admitted': return 'error';
      default: return 'default';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    fetchStats();
    fetchStudents();
  }, [entranceStatusFilter, admissionStatusFilter, searchTerm]);

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Admission Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage approved students and update entrance and admission status
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#2196f3' }}>
                  <School />
                </Avatar>
                <Box>
                  <Typography variant="h4">{stats.total_approved}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Approved</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#4caf50' }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h4">{stats.pass}</Typography>
                  <Typography variant="body2" color="text.secondary">Pass</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#ff9800' }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h4">{stats.waiting}</Typography>
                  <Typography variant="body2" color="text.secondary">Waiting</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#9c27b0' }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h4">{stats.admitted}</Typography>
                  <Typography variant="body2" color="text.secondary">Admitted</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Entrance Status</InputLabel>
                <Select
                  value={entranceStatusFilter}
                  label="Entrance Status"
                  onChange={(e) => setEntranceStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pass">Pass</MenuItem>
                  <MenuItem value="waiting">Waiting</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="absent">Absent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Admission Status</InputLabel>
                <Select
                  value={admissionStatusFilter}
                  label="Admission Status"
                  onChange={(e) => setAdmissionStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="admitted">Admitted</MenuItem>
                  <MenuItem value="not_admitted">Not Admitted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, or tracker ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchStudents}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Students Table */}
      <Card>
        <CardContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Applied Class</TableCell>
                  <TableCell>Entrance Status</TableCell>
                  <TableCell>Admission Status</TableCell>
                  <TableCell>Application Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student.student_id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {student.first_name} {student.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {student.tracker_id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{student.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {student.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{student.applied_class}</TableCell>
                    <TableCell>
                      <Chip
                        label={student.entrance_status?.toUpperCase() || 'PENDING'}
                        color={getEntranceStatusColor(student.entrance_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={student.admission_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                        color={getAdmissionStatusColor(student.admission_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(student.created_at)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              fetchStudentDetails(student.student_id);
                              setViewDialogOpen(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Status">
                          <IconButton
                            size="small"
                            onClick={() => {
                              fetchStudentDetails(student.student_id);
                              setStatusDialogOpen(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {students.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                No approved students found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* View Student Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Student Details</DialogTitle>
        <DialogContent>
          {selectedStudent && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Personal Information</Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2"><strong>Name:</strong> {selectedStudent.first_name} {selectedStudent.last_name}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {selectedStudent.email}</Typography>
                  <Typography variant="body2"><strong>Phone:</strong> {selectedStudent.phone}</Typography>
                  <Typography variant="body2"><strong>Date of Birth:</strong> {formatDate(selectedStudent.date_of_birth)}</Typography>
                  <Typography variant="body2"><strong>Tracker ID:</strong> {selectedStudent.tracker_id}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Application Information</Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2"><strong>Applied Class:</strong> {selectedStudent.applied_class}</Typography>
                  <Typography variant="body2"><strong>Entrance Status:</strong> 
                    <Chip
                      label={selectedStudent.entrance_status?.toUpperCase() || 'PENDING'}
                      color={getEntranceStatusColor(selectedStudent.entrance_status)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="body2"><strong>Admission Status:</strong> 
                    <Chip
                      label={selectedStudent.admission_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                      color={getAdmissionStatusColor(selectedStudent.admission_status)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="body2"><strong>Application Date:</strong> {formatDate(selectedStudent.created_at)}</Typography>
                </Box>
              </Grid>
              
              {selectedStudent.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                  <Typography variant="body2">{selectedStudent.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Student Status</DialogTitle>
        <DialogContent>
          {selectedStudent && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Student:</strong> {selectedStudent.first_name} {selectedStudent.last_name}
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Entrance Status</InputLabel>
                <Select
                  value={statusForm.entrance_status}
                  label="Entrance Status"
                  onChange={(e) => setStatusForm({ ...statusForm, entrance_status: e.target.value })}
                >
                  <MenuItem value="pass">Pass</MenuItem>
                  <MenuItem value="waiting">Waiting</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="absent">Absent</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Admission Status</InputLabel>
                <Select
                  value={statusForm.admission_status}
                  label="Admission Status"
                  onChange={(e) => setStatusForm({ ...statusForm, admission_status: e.target.value })}
                >
                  <MenuItem value="admitted">Admitted</MenuItem>
                  <MenuItem value="not_admitted">Not Admitted</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={statusForm.notes}
                onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
              />
              
              {statusForm.admission_status === 'admitted' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Student user account will be created automatically upon admission.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={updateStudentStatus} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdmissionManagement;
